import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useContext,
} from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import styled from "styled-components";
import API_ENDPOINTS from "../config/api";
import { DirectionContext } from "../App";

const { KEYS: COOKIE_KEYS } = API_ENDPOINTS.COOKIE_CONFIG;

// 페이지 애니메이션 변형 설정
const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      type: "tween",
      ease: "easeOut",
      duration: 0.4,
    },
  },
  exit: {
    opacity: 0,
    y: -30,
    transition: {
      duration: 0.3,
    },
  },
};

// 인앱웹뷰 감지 함수
const isInAppBrowser = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return /wv|KAKAOTALK|Line|NAVER|FBAN|Instagram/.test(userAgent);
};

// 웹 환경 감지 함수 추가
const isWebEnvironment = () => {
  return window.innerWidth >= 768;
};

function IdCardSubmitPage() {
  const { navigateTo } = useContext(DirectionContext);
  const navigate = useNavigate();
  const location = useLocation();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [error, setError] = useState(null);
  const [usingFrontCamera, setUsingFrontCamera] = useState(false);
  const [isInApp, setIsInApp] = useState(false);
  const [isWeb, setIsWeb] = useState(false); // 웹 환경 감지 상태 추가
  const [permissionStatus, setPermissionStatus] = useState("prompt"); // 'prompt', 'granted', 'denied'
  const [cameraError, setCameraError] = useState(null);
  const [useAlternativeInput, setUseAlternativeInput] = useState(false);
  const [showCamera, setShowCamera] = useState(false); // 카메라 UI 표시 상태 추가
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [buttonsVisible, setButtonsVisible] = useState(false);
  const topRef = useRef(null);

  // DOM 업데이트 전에 스크롤 초기화 (useLayoutEffect 사용)
  useLayoutEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({
        behavior: "auto",
        block: "start",
      });
    }
  }, []);

  // 컴포넌트 마운트 시 인앱 브라우저 확인 및 localStorage에서 이미지 로드
  useEffect(() => {
    // 웹 환경 감지
    const webEnvironment = isWebEnvironment();
    setIsWeb(webEnvironment);

    // 웹 환경이면 항상 대체 입력 방식(파일 업로드) 사용
    // if (webEnvironment) {
    //   setUseAlternativeInput(true);
    // }

    // localStorage에서 임시 이미지 확인
    try {
      const storedImage = localStorage.getItem("temp_captured_image");
      if (storedImage) {
        setCapturedImage(storedImage);
        setButtonsVisible(true);
      }
    } catch (err) {
      console.error("localStorage에서 이미지 로드 실패:", err);
    }

    // 이전 라우터 상태에서 이미지도 함께 확인 (대체 방법)
    if (location.state && location.state.capturedImage) {
      setCapturedImage(location.state.capturedImage.imageDataURL);
      setButtonsVisible(true);
    }

    // 카메라에서 돌아온 경우 localStorage에서 이미지 다시 확인
    if (location.state && location.state.fromCamera) {
      try {
        const storedImage = localStorage.getItem("temp_captured_image");
        if (storedImage) {
          setCapturedImage(storedImage);
          setButtonsVisible(true);
        }
      } catch (err) {
        console.error("카메라에서 돌아온 후 이미지 로드 실패:", err);
      }
    }

    const inAppDetected = isInAppBrowser();
    setIsInApp(inAppDetected);

    // 인앱웹뷰에서는 대체 입력 방식 활성화
    // if (inAppDetected) {
    //   setUseAlternativeInput(true);
    // }

    // 브라우저에서 카메라 지원 여부 확인
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setUseAlternativeInput(true);
      return;
    }

    // 카메라 권한 상태 확인 (가능한 경우)
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: "camera" })
        .then((status) => {
          setPermissionStatus(status.state);
          status.onchange = () => {
            setPermissionStatus(status.state);
          };
        })
        .catch((err) => {});
    }

    // 화면 크기 변경 감지
    const handleResize = () => {
      setIsWeb(isWebEnvironment());
    };

    window.addEventListener("resize", handleResize);

    // 컴포넌트 언마운트 시 카메라 정리 및 스크롤 복원
    return () => {
      document.body.style.overflow = "auto";
      stopCamera();
      window.removeEventListener("resize", handleResize);
    };
  }, [location]);

  // 카메라 시작 함수
  const startCamera = async (forceFront = false) => {
    // 대체 입력 모드인 경우 파일 입력으로 전환
    if (useAlternativeInput) {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
      return;
    }

    try {
      // 먼저 이전에 실행 중이던 카메라 중지
      stopCamera();

      // 먼저 카메라 UI를 표시하여 videoRef 요소가 생성되도록 함
      setShowCamera(true);

      // 잠시 대기하여 DOM이 업데이트되도록 함
      await new Promise((resolve) => setTimeout(resolve, 100));

      // 여기서 비디오 요소가 존재하는지 다시 확인
      if (!videoRef.current) {
        // 약간의 지연 후 다시 시도
        setTimeout(() => {
          initializeCamera(forceFront);
        }, 300);
        return;
      }

      // 비디오 요소가 존재하면 카메라 초기화 진행
      await initializeCamera(forceFront);
    } catch (err) {
      handleCameraError(err, forceFront);
    }
  };

  // 카메라 초기화 함수 (startCamera에서 분리)
  const initializeCamera = async (forceFront = false) => {
    try {
      const facingMode = forceFront ? "user" : "environment";
      setUsingFrontCamera(forceFront);

      // 다양한 제약 조건 시도
      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      };

      // 인앱웹뷰에서 카메라 접근 시 안내 메시지 표시
      if (isInApp) {
        setError(
          "카메라 접근 권한을 요청합니다. 권한 요청 팝업이 표시되면 '허용'을 선택해주세요.",
        );
      }

      // 카메라 스트림 가져오기 시도
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      // 스트림 참조 저장
      streamRef.current = stream;

      // 비디오 요소 확인 (한 번 더)
      if (!videoRef.current) {
        setCameraError(
          "비디오 요소를 찾을 수 없음 - 앱 재로드가 필요할 수 있습니다",
        );
        return;
      }

      // 비디오 요소에 스트림 연결
      videoRef.current.srcObject = stream;

      // 비디오 로드 및 재생 관련 이벤트 핸들러
      videoRef.current.onloadedmetadata = () => {
        videoRef.current
          .play()
          .then(() => {
            setCameraActive(true);
            setError(null);
            setCameraError(null);
          })
          .catch((err) => {
            setCameraError(`비디오 재생 실패: ${err.message}`);
          });
      };

      videoRef.current.onerror = (err) => {
        setCameraError(`비디오 요소 오류: ${err}`);
      };
    } catch (err) {
      handleCameraError(err, forceFront);
    }
  };

  // 카메라 오류 처리 함수
  const handleCameraError = (err, forceFront) => {
    setCameraError(`카메라 오류: ${err.name} - ${err.message}`);

    // 오류 메시지 상세화
    let errorMessage = "카메라 접근에 실패했습니다. ";

    if (err.name === "NotAllowedError") {
      errorMessage +=
        "카메라 접근 권한을 허용해주세요. 설정에서 권한을 변경할 수 있습니다.";
      setPermissionStatus("denied");
    } else if (err.name === "NotFoundError") {
      errorMessage += "카메라를 찾을 수 없습니다.";
    } else if (err.name === "NotReadableError") {
      errorMessage += "카메라가 이미 다른 앱에서 사용 중입니다.";
    } else {
      errorMessage += err.message || "알 수 없는 오류가 발생했습니다.";
    }

    // 인앱웹뷰에서 카메라 접근 실패 시 대체 입력 방식 활성화
    if (isInApp) {
      setUseAlternativeInput(true);
      errorMessage += " 이미지 업로드로 전환합니다.";
    }

    // 후면 카메라 실패 시 전면 카메라로 시도
    if (!forceFront && !useAlternativeInput) {
      try {
        startCamera(true);
        return;
      } catch (frontErr) {}
    }

    setError(errorMessage);
  };

  // 카메라 중지 함수
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setCameraActive(false);
    }
  };

  // 카메라 전환 함수
  const switchCamera = () => {
    startCamera(!usingFrontCamera);
  };

  // 사진 촬영 함수
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    try {
      // 비디오 크기에 맞게 캔버스 설정
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;

      // 비디오 프레임을 캔버스에 그리기
      const context = canvas.getContext("2d");

      // 전면 카메라의 경우 이미지 좌우 반전
      if (usingFrontCamera) {
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
      }

      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // 가이드 영역 강조를 위한 레이어 추가
      if (usingFrontCamera) {
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
      }

      // 캔버스 이미지를 데이터 URL로 변환
      const imageDataURL = canvas.toDataURL("image/jpeg");
      setCapturedImage(imageDataURL);

      // 카메라 중지
      stopCamera();
      setShowCamera(false); // 카메라 UI 숨기기
    } catch (err) {
      setError(`이미지 캡처 오류: ${err.message}`);
    }
  };

  // 카메라 페이지로 이동
  const goToCameraPage = () => {
    navigate("/id-card-camera");
  };

  // 이미지 파일 처리 함수 (파일 업로드 방식)
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      // 크롭 페이지로 이동
      navigate("/id-card-crop", {
        state: {
          originalImage: event.target.result,
        },
      });
    };
    reader.readAsDataURL(file);
  };

  // 다시 촬영하기
  // 상태만 초기화하고 카메라/앨범 선택 화면으로 돌아간다
  // (이전 버전은 자동으로 카메라를 켜거나 파일 선택을 띄웠으나,
  //  사용자가 다시 선택할 수 있도록 선택 화면을 보여주는 게 맞음)
  const handleRetakePhoto = () => {
    setCapturedImage(null);

    // 로컬 스토리지 이미지 제거
    localStorage.removeItem("temp_captured_image");

    // location.state 초기화 (history 객체 교체)
    window.history.replaceState(null, document.title, window.location.pathname);

    // 파일 입력 값도 리셋 (같은 파일 재선택 가능하도록)
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // 업로드 에러도 초기화
    setUploadError("");
  };

  const getPresignedUrls = async (files) => {
    try {
      const response = await axios.get(
        `${API_ENDPOINTS.BASE_URL}/mobile/file/upload`,
        {
          params: {
            type: "image",
            mimeType: "image/png",
            imageUploadTarget: "claimIdCardImage",
            num: files.length,
          },
          headers: {
            Authorization: `Bearer ${Cookies.get(COOKIE_KEYS.ACCESS_TOKEN)}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // 사용자 정보 및 applicationId 가져오기 (쿠키 값 검증 후 사용)
  const getUserDataAndApplicationId = async () => {
    try {
      const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);
      if (!token) {
        throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
      }

      // 1. 쿠키에서 applicationId 가져오기
      const cookieApplicationId = Cookies.get("application_id");
      if (!cookieApplicationId) {
        // 쿠키에 applicationId가 없으면 ScreenshotSubmission으로 리다이렉트
        navigateTo("/screenshot-submission");
        throw new Error("신청서를 찾을 수 없습니다. 다시 시작해주세요.");
      }

      const numericCookieId = Number(cookieApplicationId);
      if (isNaN(numericCookieId)) {
        // 쿠키 값이 유효하지 않으면 ScreenshotSubmission으로 리다이렉트
        navigateTo("/screenshot-submission");
        throw new Error("신청서 정보가 올바르지 않습니다. 다시 시작해주세요.");
      }

      // 2. 서버에서 현재 사용자의 신청서 정보 가져오기
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/clients/me`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("사용자 정보를 가져오는데 실패했습니다.");
      }

      const data = await response.json();

      // 3. 쿠키의 applicationId가 서버의 신청서 목록에 있는지 확인
      if (!data.applications || data.applications.length === 0) {
        // 신청서가 없으면 ScreenshotSubmission으로 리다이렉트
        navigateTo("/screenshot-submission");
        throw new Error("신청서를 찾을 수 없습니다. 다시 시작해주세요.");
      }

      // 쿠키의 applicationId가 신청서 목록에 있는지 확인
      const foundApplication = data.applications.find(
        (app) => Number(app.id) === numericCookieId,
      );

      if (!foundApplication) {
        // 쿠키의 applicationId가 서버 목록에 없으면 잘못된 것이므로 ScreenshotSubmission으로 리다이렉트
        navigateTo("/screenshot-submission");
        throw new Error("신청서 정보가 일치하지 않습니다. 다시 시작해주세요.");
      }

      // 4. 검증 완료: 쿠키의 applicationId 사용
      return {
        userData: data,
        applicationId: numericCookieId,
        name: data.name,
        phone: data.phoneNumber,
      };
    } catch (error) {
      console.error("사용자 정보 및 applicationId 가져오기 오류:", error);
      throw error;
    }
  };

  // base64 데이터 URL을 Blob으로 변환
  const dataURLtoBlob = (dataURL) => {
    const arr = dataURL.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleSubmitIdCard = async () => {
    if (!capturedImage) {
      if (useAlternativeInput) {
        if (fileInputRef.current) {
          fileInputRef.current.click();
        }
      } else {
        goToCameraPage();
      }
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      // 1. 사용자 정보 및 applicationId 가져오기
      const userInfo = await getUserDataAndApplicationId();

      if (!userInfo || !userInfo.applicationId) {
        throw new Error("신청서 ID를 가져오는데 실패했습니다.");
      }

      const { applicationId, name, phone } = userInfo;

      // 2. base64 이미지를 Blob으로 변환
      const blob = dataURLtoBlob(capturedImage);

      const formData = new FormData();
      formData.append("file", blob, "id-card.png");

      const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);
      if (!token) {
        throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
      }

      // 3. 신분증 이미지 업로드
      // POST /uploads/lawsuit/{lawsuitCode}/{documentType}?applicationId={id}
      const uploadUrl = `${API_ENDPOINTS.BASE_URL}/uploads/lawsuit/duo/id-card?applicationId=${applicationId}`;

      const response = await fetch(uploadUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.status !== 201) {
        const errorData = await response.json();
        throw new Error(errorData.message || "신분증 업로드에 실패했습니다.");
      }

      // localStorage에서 임시 이미지 정리
      try {
        localStorage.removeItem("temp_captured_image");
      } catch (err) {
        console.error("localStorage 정리 실패:", err);
      }

      // 4. 공동신청자 검증 API 호출
      if (!name || !phone || !applicationId) {
        throw new Error("사용자 정보를 가져오는데 실패했습니다.");
      }

      const verifyResponse = await fetch(
        `${API_ENDPOINTS.BASE_URL}/co-signer/verify`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: name,
            phone: phone,
            applicationId: applicationId,
          }),
        },
      );

      if (!verifyResponse.ok) {
        const errorData = await verifyResponse.json();
        throw new Error(
          errorData.message ||
            "공동신청자 검증에 실패했습니다. 다시 시도해주세요.",
        );
      }

      const verifyData = await verifyResponse.json();

      // 5. 검증 결과에 따라 분기 처리
      if (verifyData.success === true) {
        // 공동서명자인 경우 완료 페이지로 이동
        navigateTo("/complete");
        // navigateTo("/payment-methods");
      } else if (
        verifyData.success === false &&
        verifyData.message === "공동서명자가 아닙니다."
      ) {
        // 공동서명자가 아닌 경우 결제 방법 선택 페이지로 이동
        navigateTo("/payment-methods");
      } else {
        // 기타 오류인 경우 결제 방법 선택 페이지로 이동
        navigateTo("/payment-methods");
      }
    } catch (error) {
      console.error("신분증 업로드 오류:", error);
      setUploadError(error.message || "신분증 업로드 중 오류가 발생했습니다.");
      setIsUploading(false);
    }
  };

  // 이미지 로드 완료 핸들러 추가
  const handleImageLoad = () => {
    setImageLoaded(true);
    setButtonsVisible(true);
  };

  // 파일 업로드 안내 UI
  const renderFileUploadGuide = () => {
    if (useAlternativeInput && !capturedImage) {
      return (
        <div className="file-upload-guide">
          <h3>신분증 이미지 업로드</h3>
          <p>신분증 사진을 업로드해주세요</p>
          <p>
            카메라 촬영이 어려운 환경에서는 이미지 파일을 직접 선택할 수
            있습니다.
          </p>
          <UploadButton
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 16L8.586 11.414C8.96103 11.0391 9.46967 10.8284 10 10.8284C10.5303 10.8284 11.039 11.0391 11.414 11.414L16 16M14 14L15.586 12.414C15.961 12.0391 16.4697 11.8284 17 11.8284C17.5303 11.8284 18.039 12.0391 18.414 12.414L20 14M14 8H14.01M6 20H18C18.5304 20 19.0391 19.7893 19.4142 19.4142C19.7893 19.0391 20 18.5304 20 18V6C20 5.46957 19.7893 4.96086 19.4142 4.58579C19.0391 4.21071 18.5304 4 18 4H6C5.46957 4 4.96086 4.21071 4.58579 4.58579C4.21071 4.96086 4 5.46957 4 6V18C4 18.5304 4.21071 19.0391 4.58579 19.4142C4.96086 19.7893 5.46957 20 6 20Z"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            이미지 선택하기
          </UploadButton>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*"
            style={{ display: "none" }}
          />
        </div>
      );
    }
    return null;
  };

  // 신분증 이미지가 없는 경우 가이드 표시
  const renderIdCardGuide = () => {
    if (!capturedImage && !useAlternativeInput) {
      return (
        <IdCardPlaceholder>
          <IdCardIcon src="/png/IdCard.png" alt="신분증 아이콘" />
          <GuideImageContainer>
            <GuideImage
              src="/png/idString1.png"
              alt="신분증 촬영 가이드"
              width={960}
              height={270}
            />
            <GuideImage
              src="/png/idString.png"
              alt="신분증 촬영 가이드"
              width={1020}
              height={399}
            />
          </GuideImageContainer>
        </IdCardPlaceholder>
      );
    }
    return null;
  };

  // UI에서 이미지를 표시할 때는 state의 캡처된 이미지와 localStorage에서 전달받은 이미지 모두 확인
  const displayCapturedImage = capturedImage;

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <Content>
          {/* 신분증 이미지 또는 가이드 */}
          {displayCapturedImage ? (
            <IdCardImageContainer>
              <IdCardPreview>
                <img
                  src={displayCapturedImage}
                  alt="촬영된 신분증"
                  onLoad={handleImageLoad}
                />
              </IdCardPreview>
            </IdCardImageContainer>
          ) : (
            <>
              {renderIdCardGuide()}
              {renderFileUploadGuide()}
            </>
          )}

          {error && <ErrorMessage>{error}</ErrorMessage>}

          {/* 촬영된 이미지가 있는 경우 버튼 표시 */}
          {displayCapturedImage ? (
            <ButtonsContainer>
              <SubmitButton onClick={handleSubmitIdCard} disabled={isUploading}>
                {isUploading ? "업로드 중..." : "신분증 제출하기"}
              </SubmitButton>
              <RetakeButton onClick={handleRetakePhoto} disabled={isUploading}>
                {isWeb ? "다시 선택하기" : "다시 촬영하기"}
              </RetakeButton>
              {uploadError && <ErrorMessage>{uploadError}</ErrorMessage>}
            </ButtonsContainer>
          ) : (
            <ButtonsContainer>
              {/* {!isWeb && ( */}
              <>
                <TakePhotoButton onClick={handleSubmitIdCard}>
                  신분증 촬영하기
                </TakePhotoButton>
                <SelectFromAlbumButton
                  onClick={() => {
                    if (fileInputRef.current) {
                      fileInputRef.current.click();
                    }
                  }}
                >
                  앨범에서 선택하기
                </SelectFromAlbumButton>
              </>
              {/* )} */}
            </ButtonsContainer>
          )}

          {/* 파일 업로드 hidden input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*"
            style={{ display: "none" }}
          />
        </Content>
      </Container>
    </PageBackground>
  );
}

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #f7f7f7;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    background-color: #f7f7f7;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f7f7f7;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 60px 20px 80px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const IdCardPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 10px;
`;

const IdCardIcon = styled.img`
  width: 130px;
  height: 130px;
  margin-bottom: 30px;
  margin-top: 20px;
`;

const GuideImageContainer = styled.div`
  width: 100%;
  max-width: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  padding: 0;
`;

const GuideImage = styled.img`
  width: 100%;
  max-width: 360px;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 15px;
  }
`;

const IdCardImageContainer = styled.div`
  margin-top: 10px;
  padding-bottom: 20px;
  width: 100%;
`;

const IdCardPreview = styled.div`
  width: 100%;

  img {
    width: 100%;
    height: auto;
    aspect-ratio: 325/208;
    object-fit: contain;
    background-color: #ffffff;
    border-radius: 12px;
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.05);
  }
`;

const ErrorMessage = styled.div`
  color: #ff0000;
  font-size: 14px;
  margin-top: 10px;
  text-align: center;
`;

const ButtonsContainer = styled.div`
  margin-top: 20px;
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
  margin: 40px auto 40px auto;
`;

const RetakeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  height: 56px;
  width: 100%;
  max-width: 400px;
  border-radius: 20px;
  border: none;
  font-size: 16px;
  font-weight: 600;
  margin: 0 auto;
  cursor: pointer;
  background-color: white;
  color: #e31c20;
  transition: all 0.2s ease;

  img {
    width: 20px;
    height: 20px;
    margin-right: 8px;
    pointer-events: none;
  }

  &:hover {
    background-color: #f5f5f5;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  height: 56px;
  width: 100%;
  max-width: 400px;
  border-radius: 20px;
  border: none;
  font-size: 16px;
  font-weight: 600;
  margin: 0 auto 12px auto;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  background-color: ${(props) => (props.disabled ? "#E0898A" : "#E31C20")};
  color: ${(props) => (props.disabled ? "#DDC2C2" : "white")};
  transition: all 0.2s ease;
  opacity: ${(props) => (props.disabled ? 1 : 1)};

  &:hover {
    background-color: ${(props) => (props.disabled ? "#E0898A" : "#B01418")};
  }

  &:active {
    transform: ${(props) => (props.disabled ? "none" : "scale(0.98)")};
  }
`;

const UploadButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 20px;
  height: 56px;
  width: 100%;
  max-width: 400px;
  border-radius: 20px;
  border: none;
  font-size: 16px;
  font-weight: 600;
  margin: 0 auto;
  cursor: pointer;
  background-color: #e31c20;
  color: white;
  transition: all 0.2s ease;

  svg {
    flex-shrink: 0;
  }

  &:hover {
    background-color: #b01418;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const TakePhotoButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  height: 56px;
  width: 100%;
  max-width: 400px;
  border-radius: 20px;
  border: none;
  font-size: 16px;
  font-weight: 600;
  margin: 0 auto;
  cursor: pointer;
  background-color: #e31c20;
  color: white;
  transition: all 0.2s ease;

  img {
    width: 24px;
    height: 24px;
    margin-right: 10px;
    pointer-events: none;
  }

  &:hover {
    background-color: #b01418;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const SelectFromAlbumButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  height: 56px;
  width: 100%;
  max-width: 400px;
  border-radius: 20px;
  border: 1px solid #e5e5e5;
  font-size: 16px;
  font-weight: 600;
  margin: 12px auto 0 auto;
  cursor: pointer;
  background-color: white;
  color: #e31c20;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
  }

  &:active {
    transform: scale(0.98);
  }
`;

export default IdCardSubmitPage;
